'use client';

import { Skeleton } from '@/components/ui/skeleton';
import { LoadingStates } from '@/components/loading-states';
import { ProcessHistoryHeader } from '@/components/history/process-history-header';
import { ProcessHistoryTaskList } from '@/components/history/process-history-task-list';
import { useProcessHistory } from '@/components/history/use-process-history';
import {
  HISTORIC_FORBIDDEN_TITLE,
  HISTORIC_PROCESS_FORBIDDEN_MESSAGE,
  useHistoricAccessDeniedRedirect,
} from '@/components/history/use-historic-access-denied-redirect';

interface ProcessHistoryPageProps {
  params: {
    processInstanceId: string;
  };
}

export default function ProcessHistoryPage({ params }: ProcessHistoryPageProps) {
  const { processInstanceId } = params;
  const { data, loading, error, forbidden } = useProcessHistory(processInstanceId);

  useHistoricAccessDeniedRedirect(!loading && forbidden, HISTORIC_PROCESS_FORBIDDEN_MESSAGE);

  if (loading) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-3 px-3 py-3">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="h-full" aria-live="polite">
        <span className="sr-only">{HISTORIC_FORBIDDEN_TITLE}</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-full items-center justify-center px-3 py-3">
        <LoadingStates error={error ?? 'No se pudo cargar el historial del caso.'} />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 w-full max-w-none flex-col gap-4 px-3 py-3">
      <ProcessHistoryHeader process={data.process} accessReasons={data.accessReasons} />
      <div className="min-h-0 flex-1">
        <h2 className="mb-3 text-lg font-semibold">Formularios</h2>
        <ProcessHistoryTaskList
          processInstanceId={processInstanceId}
          tasks={data.tasks}
        />
      </div>
    </div>
  );
}
